import { useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../../popover";
import { Button } from "../../button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import TableFilterState from "./table-filter-state";

type OptionType = { value: string; label: string };

type PopoverSingleSelectFilterProps<T = any> = {
  filterState: TableFilterState<T>;
  options: OptionType[];
  placeholder?: string;
  title?: string;
  description?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const PopoverSingleSelectFilter = <T extends Record<string, any>>({
  filterState,
  options,
  placeholder = "Select...",
  title,
  description,
  isOpen = false,
  onOpenChange
}: PopoverSingleSelectFilterProps<T>) => {
  // Check if filter has value (empty string means "all" which is not a filter)
  const hasValue = filterState.filter[filterState.filterKey] && filterState.filter[filterState.filterKey] !== '';

  // Use controlled state if provided, otherwise use internal logic
  const controlledOpen = isOpen !== undefined ? isOpen : (hasValue || false);
  const controlledOnOpenChange = onOpenChange || (() => {});

  const [selectedValue, setSelectedValue] = useState<string>(
    filterState.filter[filterState.filterKey] === '' ? 'all' : (filterState.filter[filterState.filterKey] || "")
  );

  useEffect(() => {
    setSelectedValue(filterState.filter[filterState.filterKey] === '' ? 'all' : (filterState.filter[filterState.filterKey] || ""));
  }, [filterState.filter, filterState.filterKey]);

  const handleOpenChange = (newOpen: boolean) => {
    controlledOnOpenChange(newOpen);
  };

  const handleApply = () => {
    // If "all" is selected, treat it as clearing the filter
    const valueToSet = selectedValue === 'all' ? '' : selectedValue;
    filterState.setFilter((prev: any) => ({
      ...prev,
      [filterState.filterKey]: valueToSet,
    }));
    controlledOnOpenChange(false);
  };

  const handleClear = () => {
    filterState.setFilter((prev: any) => ({
      ...prev,
      [filterState.filterKey]: "",
    }));
    setSelectedValue("");
    controlledOnOpenChange(false);
  };

  // Don't allow empty string as value for SelectItem, use undefined instead
  const displayValue = selectedValue || undefined;

  return (
    <Popover open={controlledOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <img className="ml-2 w-3 cursor-pointer" src={MagnifyingGlass} alt="Filter" />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4">
        <div className="space-y-4">
          {title && (
            <div className="text-xs text-[#6A7282] font-medium">
              {title}
            </div>
          )}

          <Select value={displayValue} onValueChange={setSelectedValue}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {description && (
            <div className="text-xs text-gray-500">
              {description}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              className="flex-1 text-xs"
              size="sm"
              onClick={handleApply}
            >
              Apply
            </Button>
            <Button
              className="flex-1 text-xs"
              variant="outline"
              size="sm"
              onClick={handleClear}
            >
              Clear
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PopoverSingleSelectFilter;
